import { Extension, type Range } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { Editor } from '@tiptap/react';
import { useEffect, useRef, useState } from 'react';
import { de } from '../../i18n/de';
import { useEditorDom } from '../../lib/editorDom';
import { spanRanges, type SpanRange } from '../../lib/refSpan';

const focusKey = new PluginKey<DecorationSet>('spanFocus');

/** Die Stelle eines Bezugs leicht getönt, solange man über seine Randlinie fährt (`tr.setMeta(focusKey, range | null)`). */
export const spanFocus = Extension.create({
  name: 'spanFocus',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: focusKey,
        state: {
          init: () => DecorationSet.empty,
          apply: (tr, old) => {
            const meta = tr.getMeta(focusKey) as Range | null | undefined;
            if (meta === null) return DecorationSet.empty;
            if (meta) return DecorationSet.create(tr.doc, [Decoration.inline(meta.from, meta.to, { class: 'rt-span-focus' })]);
            return old.map(tr.mapping, tr.doc);
          },
        },
        props: { decorations: (state) => focusKey.getState(state) },
      }),
    ];
  },
});

interface Bar extends SpanRange {
  top: number;
  height: number;
  /** Lage der Linie (links vom Text) */
  x: number;
  /** Platz links vom Text (für das Namensschild) */
  room: number;
  /** nebeneinander, wenn sich Bezüge in denselben Zeilen überschneiden */
  lane: number;
}

/** Knappes Schild bei wenig Platz: der erste Name mit seinem ersten Wort, weitere als „+1“ („Clara +1“) */
const shortLabel = (names: string[]): string => `${names[0]?.replace(/^@/, '').split(/\s+/)[0] ?? ''}${names.length > 1 ? ` +${names.length - 1}` : ''}`;

/**
 * Randlinie der Bezüge: neben jeder Stelle, die zu Elementen gehört, eine dünne Linie am linken Rand der Seite mit einem Namensschild
 * („Clara · Rede“). Der Text bleibt ruhig; Überfahren tönt die Stelle und zeigt am Schild ein ✕ (Bezug lösen), ein Klick auf das Schild
 * öffnet den Referenz-Browser zum Ändern.
 */
export function SpanGutter({ editor, name, onOpen, onRemove }: { editor: Editor; name: (key: string) => string; onOpen: (span: SpanRange) => void; onRemove: (span: SpanRange) => void }) {
  const host = useRef<HTMLDivElement>(null);
  const [bars, setBars] = useState<Bar[]>([]);
  // gemessen wird erst, wenn der Editor eingehängt ist
  const dom = useEditorDom(editor);

  useEffect(() => {
    if (!dom) return;
    let frame = 0;
    const measure = (): void => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const el = host.current?.parentElement;
        if (!el || editor.isDestroyed) return;
        const box = el.getBoundingClientRect();
        const page = dom;
        const room = parseFloat(getComputedStyle(page).paddingLeft) || 0;
        const textLeft = page.getBoundingClientRect().left - box.left + room;
        const out: Bar[] = [];
        for (const s of spanRanges(editor.state.doc)) {
          const a = editor.view.coordsAtPos(s.from, 1);
          const b = editor.view.coordsAtPos(s.to, -1);
          const top = a.top - box.top;
          const height = Math.max(b.bottom - a.top, a.bottom - a.top);
          const lane = out.filter((o) => o.top < top + height && o.top + o.height > top).length;
          out.push({ ...s, top, height, x: textLeft - 14 - lane * 6, room, lane });
        }
        setBars(out);
      });
    };
    measure();
    const onTransaction = ({ transaction }: { transaction: { docChanged: boolean } }): void => {
      if (transaction.docChanged) measure();
    };
    editor.on('transaction', onTransaction);
    const ro = new ResizeObserver(measure);
    ro.observe(dom);
    return () => {
      editor.off('transaction', onTransaction);
      ro.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [editor, dom]);

  const focus = (s: SpanRange | null): void => {
    if (dom && !editor.isDestroyed) editor.view.dispatch(editor.state.tr.setMeta(focusKey, s ? { from: s.from, to: s.to } : null));
  };

  return (
    <div ref={host} className="rt-span-gutter">
      {bars.map((b) => {
        const names = b.keys.map(name);
        const full = `${names.join(' · ')}${b.role ? ` · ${de.refBrowser.roles[b.role]}` : ''}`;
        const roomy = b.room >= 120;
        return (
          <div
            key={`${b.from}:${b.to}`}
            className="rt-span-bar"
            style={{ left: b.x, top: b.top, height: b.height }}
            onMouseEnter={() => focus(b)}
            onMouseLeave={() => focus(null)}
            data-testid="span-bar"
            data-keys={b.keys.join(' ')}
            data-role={b.role ?? ''}
          >
            <span className="rt-span-tag" style={{ top: b.lane * 20 }}>
              <button
                type="button"
                className="rt-span-remove"
                title={de.editor.removeSpan}
                aria-label={de.editor.removeSpan}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  focus(null);
                  onRemove(b);
                }}
                data-testid="span-remove"
              >
                ✕
              </button>
              <button
                type="button"
                className="rt-span-label"
                style={{ maxWidth: Math.max(48, b.room + 8 - b.lane * 6) }}
                title={de.refBrowser.spanLabel(full)}
                aria-label={de.refBrowser.spanLabel(full)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  focus(null);
                  onOpen(b);
                }}
                data-testid="span-label"
              >
                {roomy ? full : shortLabel(names)}
              </button>
            </span>
          </div>
        );
      })}
    </div>
  );
}
